import { useEffect, useState } from 'react';
import { GATEWAY_WS_URL } from '../config/api';

/**
 * Watch-only live view of a gateway browser session (a Sente run's browser).
 *
 * Stripped from useTaskExecution's WS client: same /gateway protocol —
 * connection_ready → connect {type:'screenshot'} → connection_ready → attach_to_session —
 * but only consumes `frame`/`screenshot` (JPEG stream) and `action_executed` (url updates;
 * the agent protocol has no message carrying a page title). Reconnects every 3s while
 * `active` (attach fails until the agent socket is up, so retrying doubles as "wait for the
 * session to become watchable"). Pings every 30s: an idle session emits no frames (CDP
 * screencast only fires on repaint), and a byte-less socket gets dropped by intermediaries.
 */
export function useRunStream(sessionId: string | null | undefined, active: boolean) {
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);
  const [currentUrl, setCurrentUrl] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [attempt, setAttempt] = useState(0);

  // A backgrounded tab throttles the keepalive until intermediaries drop the socket;
  // force a fresh connect+attach as soon as the tab is visible again.
  useEffect(() => {
    if (!sessionId || !active) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') setAttempt(a => a + 1);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [sessionId, active]);

  useEffect(() => {
    if (!sessionId || !active) return;

    let unmounted = false;
    let retry: ReturnType<typeof setTimeout> | null = null;
    const ws = new WebSocket(GATEWAY_WS_URL);
    let connectionId: string | null = null;
    let hasAttached = false;
    // No top-level sessionId on the ping — the gateway forwards sessionId-bearing messages to the agent.
    const keepalive = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'ping', timestamp: new Date().toISOString() }));
      }
    }, 30_000);

    ws.onmessage = event => {
      try {
        const message = JSON.parse(event.data);
        if (message.type === 'connection_ready' && !connectionId) {
          connectionId = message.payload.connectionId;
          ws.send(
            JSON.stringify({
              type: 'connect',
              payload: { clientId: connectionId, type: 'screenshot' },
              timestamp: new Date().toISOString(),
            }),
          );
          return;
        }
        if (message.type === 'connection_ready' && message.payload.message && !hasAttached) {
          hasAttached = true;
          ws.send(
            JSON.stringify({
              type: 'attach_to_session',
              payload: { sessionId },
              timestamp: new Date().toISOString(),
            }),
          );
          return;
        }
        if (message.type === 'connection_established') {
          setIsConnected(true);
        }
        if (message.type === 'connection_error') {
          ws.close(); // session not attachable (yet) — onclose schedules the retry
        }
        if (message.type === 'frame' || message.type === 'screenshot') {
          const data = message.payload.imageData || message.payload.data;
          if (data) setScreenshotUrl(`data:image/jpeg;base64,${data}`);
        }
        if (message.type === 'action_executed') {
          const url = message.payload?.urlAfter || message.payload?.urlBefore;
          if (url) setCurrentUrl(url);
        }
        if (message.type === 'manual_intervention' && message.payload?.currentUrl) {
          setCurrentUrl(message.payload.currentUrl);
        }
      } catch {
        // ignore malformed messages
      }
    };
    ws.onerror = () => ws.close();
    ws.onclose = () => {
      setIsConnected(false);
      if (!unmounted) retry = setTimeout(() => setAttempt(a => a + 1), 3000);
    };

    return () => {
      unmounted = true;
      clearInterval(keepalive);
      if (retry) clearTimeout(retry);
      ws.onclose = null;
      ws.close();
      setIsConnected(false);
    };
  }, [sessionId, active, attempt]);

  return { screenshotUrl, currentUrl, isConnected };
}
